/* =============================================================================
   CORE / TABLE-PLAN — Pure-Logik für den Gast-Wunschtisch-Plan (S204 Cut 3)
   =============================================================================
   Window-frei + testbar. Der Gast wählt beim Reservieren OPTIONAL einen
   Wunschtisch auf dem Raumplan — unverbindliche Anfrage, die verbindliche
   Platzierung bleibt beim Annahmeprozess. Datensparsam: pro Kachel nur Name,
   „bis X Pers." und frei/belegt. Diese Helfer bestimmen Kachel-Status +
   Bereichs-/Segment-Logik; der Renderer (customer-plan.js) ist window-gebunden.
============================================================================ */

// Bereiche wie im Belegungs-Snapshot (S199 Cut 2). indoor immer verfügbar,
// outdoor/club nur bei aktivierter Einstellung.
export const PLAN_AREAS = [
  { id: 'indoor',  label: 'Innen' },
  { id: 'outdoor', label: 'Außen' },
  { id: 'club',    label: 'Club' }
];

/** Verfügbare Bereiche laut Settings (Muster enabledSnapAreas). */
export function enabledPlanAreas(settings) {
  const s = settings || {};
  return PLAN_AREAS.filter(a =>
    a.id === 'indoor' ||
    (a.id === 'outdoor' && !!s.outdoorEnabled) ||
    (a.id === 'club' && !!s.clubEnabled)
  );
}

/** Segment-Wahl → reservations.area. Kein/ungültiges Segment = „Egal" (any). */
export function segmentToArea(seg) {
  return (seg === 'indoor' || seg === 'outdoor' || seg === 'club') ? seg : 'any';
}

/** area (inkl. „any") → aktives Segment für die UI (any → null = kein Segment). */
export function areaToSegment(area) {
  return (area === 'indoor' || area === 'outdoor' || area === 'club') ? area : null;
}

/**
 * Max. Personen eines Tisches = Sitzplätze + (erweiterbar ? Zusatzstühle : 0).
 * Spiegelt tables.seats/extendable/extra_chairs (anon lesbar).
 */
export function tableMaxPersons(t) {
  if (!t) return 0;
  const seats = Number(t.seats) || 0;
  const extra = t.extendable ? (Number(t.extraChairs) || 0) : 0;
  return seats + extra;
}

/**
 * Kachel-Status eines Tisches im gewählten Slot:
 *   'busy'      → im Slot belegt/verplant (busySet enthält id) → ausgegraut
 *   'too-small' → max. Personen < Gästezahl → ausgegraut („zu klein")
 *   'free'      → antippbar
 * Präzedenz: belegt vor zu-klein (belegt ist die härtere Sperre). `selectable`
 * nur bei 'free'. `disabled`-Tische werden vom Renderer gar nicht erst gezeigt.
 */
export function tileStatus(table, busySet, guests) {
  const set = busySet instanceof Set ? busySet : new Set(busySet || []);
  const g = Number(guests) || 0;
  if (set.has(table && table.id)) return { status: 'busy', selectable: false };
  if (tableMaxPersons(table) < g) return { status: 'too-small', selectable: false };
  return { status: 'free', selectable: true };
}

/** Sichtbare (nicht-disabled) Tische eines Bereichs — disabled gar nicht rendern. */
export function visiblePlanTables(tables, area) {
  return (Array.isArray(tables) ? tables : [])
    .filter(t => t && t.disabled !== true && t.area === area);
}

/**
 * Große-Gruppen-Gate: gibt es im Bereich KEINEN Tisch, der die Gästezahl allein
 * fasst (alle zu klein)? Dann ist ein Wunschtisch nicht sinnvoll — Reservierung
 * bleibt möglich (Operator kombiniert). Nur true, wenn überhaupt Tische da sind.
 */
export function noSingleTableFits(tables, area, guests) {
  const vis = visiblePlanTables(tables, area);
  if (!vis.length) return false;
  const g = Number(guests) || 0;
  return vis.every(t => tableMaxPersons(t) < g);
}

/** Wunsch-Toggle: erneuter Tipp auf den gewählten Tisch wählt ab (null). */
export function toggleWish(current, tappedId) {
  return current === tappedId ? null : tappedId;
}

/**
 * S208 — Wunsch-Re-Validierung nach Änderung von Personenzahl/Datum/Zeit.
 * Nutzt tileStatus (Single-Source): passt der Wunschtisch noch (frei + groß genug),
 * bleibt er; sonst wird er verworfen und der Grund gemeldet ('busy' | 'too-small').
 * @param {object|null} table  der aktuell gewünschte Tisch (oder null)
 * @param {Set|Array} busySet  belegte table_ids im Slot
 * @param {number} guests      aktuelle Personenzahl
 * @returns {{keep: boolean, reason: 'busy'|'too-small'|null}}
 */
export function revalidateWish(table, busySet, guests) {
  if (!table) return { keep: false, reason: null };
  const st = tileStatus(table, busySet, guests);
  if (st.selectable) return { keep: true, reason: null };
  return { keep: false, reason: st.status };   // tileStatus: 'busy' vor 'too-small'
}
